import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, View } from 'react-native';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { useTheme } from '@/context/ThemeContext';
import { loadPdfJs } from '@/lib/pdfjsLoader.web';

const MIN_SCALE = 0.5;
const MAX_SCALE = 3;

export default function PdfJsViewer({ source, title = 'PDF document', initialScale = 1.25, onError }) {
  const { colors } = useTheme();
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  const [pdf, setPdf] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(initialScale);
  const [rotation, setRotation] = useState(0);
  const [status, setStatus] = useState(source ? 'loading' : 'error');

  useEffect(() => {
    let alive = true;
    let loadingTask = null;
    setPdf(null);
    setPageNumber(1);
    setStatus(source ? 'loading' : 'error');
    if (!source) return undefined;
    loadPdfJs().then((pdfjs) => {
      if (!alive) return null;
      loadingTask = pdfjs.getDocument({ url: source });
      return loadingTask.promise;
    }).then((document) => {
      if (!document) return;
      if (!alive) return;
      setPdf(document);
      setStatus('ready');
    }).catch((error) => {
      if (!alive) return;
      setStatus('error');
      onError?.(error);
    });
    return () => {
      alive = false;
      renderTaskRef.current?.cancel?.();
      loadingTask?.destroy?.();
    };
  }, [source, onError]);

  useEffect(() => {
    if (!pdf || !canvasRef.current) return undefined;
    let alive = true;
    pdf.getPage(pageNumber).then((page) => {
      if (!alive || !canvasRef.current) return null;
      const viewport = page.getViewport({ scale, rotation });
      const canvas = canvasRef.current;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(viewport.width * ratio);
      canvas.height = Math.floor(viewport.height * ratio);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      const context = canvas.getContext('2d');
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      const task = page.render({ canvasContext: context, viewport });
      renderTaskRef.current = task;
      return task.promise;
    }).catch((error) => {
      if (error?.name !== 'RenderingCancelledException') {
        setStatus('error');
        onError?.(error);
      }
    });
    return () => {
      alive = false;
      renderTaskRef.current?.cancel?.();
    };
  }, [pdf, pageNumber, scale, rotation, onError]);

  const styles = useMemo(() => StyleSheet.create({
    root: { flex: 1, minHeight: 0, backgroundColor: colors.solid_color_primary },
    toolbar: { minHeight: 42, paddingHorizontal: 10, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, borderBottomWidth: 1, borderBottomColor: colors.border_color_secondary },
    control: { minWidth: 32, height: 32, paddingHorizontal: 8, borderRadius: 7, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5, cursor: 'pointer', backgroundColor: colors.solid_color_secondary },
    disabled: { opacity: 0.4, cursor: 'default' },
    page: { marginHorizontal: 4 },
    scroller: { flex: 1, overflow: 'auto', alignItems: 'center', padding: 18 },
    canvas: { display: status === 'ready' ? 'block' : 'none', maxWidth: 'none', boxShadow: '0 2px 14px rgba(0,0,0,.2)' },
    status: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 10, padding: 24 },
  }), [colors, status]);

  if (status === 'error') return null;
  const pages = pdf?.numPages || 0;
  return (
    <View style={styles.root}>
      <View style={styles.toolbar} accessibilityLabel="PDF controls">
        <Pressable accessibilityRole="button" accessibilityLabel="Previous PDF page" disabled={pageNumber <= 1}
          onPress={() => setPageNumber((value) => Math.max(1, value - 1))} style={[styles.control, pageNumber <= 1 && styles.disabled]}>
          <Icon name="chevron-left" size={13} color={colors.text_color_primary} />
        </Pressable>
        <Text style={styles.page}>{pages ? `${pageNumber} / ${pages}` : '—'}</Text>
        <Pressable accessibilityRole="button" accessibilityLabel="Next PDF page" disabled={!pages || pageNumber >= pages}
          onPress={() => setPageNumber((value) => Math.min(pages, value + 1))} style={[styles.control, (!pages || pageNumber >= pages) && styles.disabled]}>
          <Icon name="chevron-right" size={13} color={colors.text_color_primary} />
        </Pressable>
        <Pressable accessibilityRole="button" accessibilityLabel="Zoom PDF out" onPress={() => setScale((value) => Math.max(MIN_SCALE, +(value - 0.25).toFixed(2)))} style={styles.control}>
          <Icon name="minus" size={13} color={colors.text_color_primary} />
        </Pressable>
        <Text>{Math.round(scale * 100)}%</Text>
        <Pressable accessibilityRole="button" accessibilityLabel="Zoom PDF in" onPress={() => setScale((value) => Math.min(MAX_SCALE, +(value + 0.25).toFixed(2)))} style={styles.control}>
          <Icon name="plus" size={13} color={colors.text_color_primary} />
        </Pressable>
        <Pressable accessibilityRole="button" accessibilityLabel="Rotate PDF clockwise" onPress={() => setRotation((value) => (value + 90) % 360)} style={styles.control}>
          <Icon name="rotate-right" size={13} color={colors.text_color_primary} />
        </Pressable>
      </View>
      {status === 'loading' ? (
        <View style={styles.status}><ActivityIndicator /><Text color="secondary">Loading PDF…</Text></View>
      ) : (
        <View style={styles.scroller}><canvas ref={canvasRef} aria-label={`${title}, page ${pageNumber}`} style={styles.canvas} /></View>
      )}
    </View>
  );
}
